import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { getMeta } from '../utils/storage';

/**
 * LocalVerifiedBadge
 *
 * Tiny status pill that lives next to the GhostComm logo. It runs an instant,
 * read-only integrity self-check on mount (and every 30 s thereafter) against
 * the user's local state and renders one of three states:
 *
 *   ✓ verified  (emerald)  — every present `ghostcomm_*` key parses into the
 *                            expected shape AND its metadata timestamps are
 *                            present, non-future, and `updatedAt >= createdAt`.
 *   ⚠ attention (amber)    — at least one check failed — e.g. a corrupt JWT,
 *                            an unparseable RSA blob, or future/missing meta.
 *                            The tooltip lists what went wrong.
 *   ? untracked (slate)    — no local `ghostcomm_*` data present (first run,
 *                            post-wipe, or signed-out state).
 *
 * Purely passive — it never mutates storage. The heavy lifting lives in the
 * Inspector / Download / Restore flows; this is the always-visible trust
 * indicator those flows feed into.
 */

const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

function checkJwtShape(value) {
  if (typeof value !== 'string') return 'Session token is not a string.';
  if (!JWT_SHAPE.test(value)) return 'Session token is not in JWT shape.';
  return null;
}

function checkKeyPair(value) {
  try {
    const parsed = JSON.parse(value);
    if (!parsed?.publicKey || !parsed?.privateKey) {
      return 'RSA keypair is missing publicKey or privateKey.';
    }
    if (parsed.publicKey?.kty !== 'RSA' || parsed.privateKey?.kty !== 'RSA') {
      return 'RSA keypair fields are not in JWK/RSA shape.';
    }
    return null;
  } catch {
    return 'RSA keypair does not parse as JSON.';
  }
}

function checkMetaTimestamps(key) {
  const meta = getMeta(key);
  if (!meta) return null; // fine — backfill happens on Inspector mount
  const now = Date.now();
  const parseIso = (s) => {
    if (!s) return null;
    const t = new Date(s).getTime();
    return Number.isFinite(t) ? t : null;
  };
  const created = parseIso(meta.createdAt);
  const updated = parseIso(meta.updatedAt);
  if (updated && updated > now + 5 * 60 * 1000) {
    return `${key} has a future updatedAt timestamp.`;
  }
  if (created && created > now + 5 * 60 * 1000) {
    return `${key} has a future createdAt timestamp.`;
  }
  if (created && updated && updated < created) {
    return `${key} has updatedAt earlier than createdAt.`;
  }
  return null;
}

function runSelfCheck() {
  const issues = [];
  let present = 0;
  const token = window.localStorage.getItem('ghostcomm_token');
  const keys = window.localStorage.getItem('ghostcomm_keys');
  if (token) {
    present += 1;
    const bad = checkJwtShape(token);
    if (bad) issues.push(bad);
    const metaBad = checkMetaTimestamps('ghostcomm_token');
    if (metaBad) issues.push(metaBad);
  }
  if (keys) {
    present += 1;
    const bad = checkKeyPair(keys);
    if (bad) issues.push(bad);
    const metaBad = checkMetaTimestamps('ghostcomm_keys');
    if (metaBad) issues.push(metaBad);
  }
  if (present === 0) {
    return { state: 'untracked', issues: [] };
  }
  return { state: issues.length ? 'attention' : 'verified', issues };
}

export const LocalVerifiedBadge = () => {
  const [result, setResult] = useState(() => runSelfCheck());

  useEffect(() => {
    const tick = () => setResult(runSelfCheck());
    // Re-check periodically so a bad write shows up without a page reload.
    const id = setInterval(tick, 30000);
    // Also re-check on storage events from other tabs.
    window.addEventListener('storage', tick);
    return () => {
      clearInterval(id);
      window.removeEventListener('storage', tick);
    };
  }, []);

  const state = result.state;
  const Icon =
    state === 'verified'
      ? ShieldCheck
      : state === 'attention'
      ? ShieldAlert
      : ShieldQuestion;

  const palette = {
    verified: {
      cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
      label: 'verified',
      tip: 'Your local data passes the integrity self-check. Every stored Government Shield key parses in the expected shape and has a sane timestamp.',
    },
    attention: {
      cls: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
      label: 'attention',
      tip: 'Something looks off in your local data. Open Settings → Local data on this device to investigate.',
    },
    untracked: {
      cls: 'text-slate-500 bg-slate-500/10 border-slate-500/30',
      label: 'no data',
      tip: 'No Government Shield data is stored on this device yet. Sign in to start a local state to protect.',
    },
  };
  const p = palette[state];

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium cursor-help ${p.cls}`}
            data-testid={`local-verified-badge-${state}`}
            aria-label={`Local data status: ${p.label}`}
          >
            <Icon className="w-3 h-3" />
            {p.label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-xs leading-relaxed">
          <p>{p.tip}</p>
          {result.issues.length > 0 && (
            <ul
              className="mt-1 pl-4 list-disc space-y-0.5 text-amber-300/90"
              data-testid="local-verified-issues"
            >
              {result.issues.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default LocalVerifiedBadge;
